import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Radio, X } from 'lucide-react';
import { useSocket } from '../../../../context/SocketContext';
import { listUsersByIds } from '../../../../services/UserSerivce';
import { getappstoroles } from '../../../../services/AppToRole';
import { getDashboardTabData } from '../../../../services/DashboardOverviewService';
import { useWaypoint } from '../waypoint/WaypointProvider.jsx';
import '../waypoint/waypoint.css';
import './online-students.css';

/* =============================================================
   Online students, live.

   Presence already exists: SocketContext holds `onlineUsers`, the auth
   user ids of everyone connected, kept current by user_online /
   user_offline. It is every signed-in user of every role, so what this
   box does is narrow that set to the students the viewer may see:

     admin    every online user whose auth profile carries the Student
              role -- the same test ListStudentUsers uses on /users.
     trainer  every online user who is a student in one of THIS
              trainer's batches. /dashboard/tab-data?tab=students
              already scopes to the caller's batches server-side and
              keeps each student's auth `userId`, which is the id
              presence is keyed by. (/trainerdashboard/mystudents
              overwrites userId with the billing id, so it cannot be
              matched against presence.)

   Nothing here asks the server who is online -- the count moves the
   moment the socket says so. The only requests are the ones that
   decide who counts: the trainer's roster once, or the auth records of
   ids not seen before.

   The figure opens the list, and every name opens the student through
   Waypoint. Waypoint's student view is keyed by the billing student
   id: the trainer roster carries it; for an admin it is looked up by
   email on click, one request for the one row asked for.
   ============================================================= */

const EMPTY = new Set();
const USER_CHUNK = 100;
const ROSTER_LIMIT = 1000;
// Presence arrives as a burst on connect -- one event per online user.
// Resolve the burst, not each event in it.
const RESOLVE_DELAY_MS = 600;

const csv = (v) =>
  (Array.isArray(v) ? v : String(v || '').split(','))
    .map((s) => String(s).trim())
    .filter(Boolean);

const isStudentUser = (user, role) => {
  if (!user) return false;
  const ids = csv(user.profileIds);
  if (role && (ids.includes(String(role.id)) || (role.value && ids.includes(String(role.value))))) {
    return true;
  }
  return (
    Array.isArray(user.profiles) &&
    user.profiles.some((p) => String(p?.label || p?.name || '').toLowerCase() === 'student')
  );
};

/* The Student role, looked up once per page load and shared. It was fetched
   in the mount effect, so every visit to the dashboard asked again -- and
   twice in development, where StrictMode mounts, unmounts and remounts.
   The role doesn't change within a session. A failure clears the promise so
   the next mount can try again rather than inherit the error. */
let studentRolePromise = null;
const loadStudentRole = () => {
  if (!studentRolePromise) {
    // Enough for every role in one page; the default of 10 could
    // leave Student off it.
    studentRolePromise = getappstoroles(500, 1)
      .then((res) => {
        const roles = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];
        return (
          roles.find((r) => r?.label === 'Student' && r?.value === 'student') ||
          roles.find((r) => String(r?.value || '').toLowerCase() === 'student') ||
          null
        );
      })
      .catch((error) => {
        studentRolePromise = null;
        throw error;
      });
  }
  return studentRolePromise;
};

const fullName = (u) =>
  `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || u?.name || u?.email || 'Unnamed student';

const OnlineStudentsBox = ({ scope = 'admin', variant = 'tile' }) => {
  const socketCtx = useSocket();
  const onlineUsers = socketCtx?.onlineUsers || EMPTY;
  const connected = Boolean(socketCtx?.isConnected);
  const { open: openEntity } = useWaypoint();
  const titleId = useId();

  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showList, setShowList] = useState(false);
  // Row key -> 'busy' while an admin's click resolves the student record,
  // 'missing' when there is no billing student behind that account.
  const [opening, setOpening] = useState({});

  // trainer: auth userId -> the row to show
  const [roster, setRoster] = useState(() => new Map());
  // admin: the Student role, and auth user id -> auth record (null when
  // the id resolved to nobody)
  const [studentRole, setStudentRole] = useState(null);
  const [users, setUsers] = useState(() => new Map());
  const inFlightRef = useRef(new Set());

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Who counts: loaded once.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        if (scope === 'trainer') {
          const resp = await getDashboardTabData({ tab: 'students', page: 1, limit: ROSTER_LIMIT });
          const items = resp?.data?.items || resp?.items || [];
          const map = new Map();
          items.forEach((s) => {
            const uid = String(s?.userId || '').trim();
            // Billing leaves userId empty, or "NA", for a student with no
            // login -- someone who can never be online.
            if (!uid || uid === 'NA') return;
            map.set(uid, {
              key: uid,
              studentId: s.id || s.studentId || null,
              name: s.name || s.studentName || 'Unnamed student',
              email: s.email || s.studentEmail || '',
              batchName: s.batchName && s.batchName !== 'N/A' ? s.batchName : '',
            });
          });
          if (alive) setRoster(map);
        } else {
          const role = await loadStudentRole();
          if (alive) {
            setStudentRole(role);
            if (!role) setFailed(true);
          }
        }
      } catch (error) {
        console.error('Online students: could not load who counts', error);
        if (alive) setFailed(true);
      } finally {
        if (alive) setReady(true);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [scope]);

  // Admin: resolve ids presence has not introduced before.
  useEffect(() => {
    if (scope !== 'admin' || !ready || failed) return undefined;
    const unknown = [...onlineUsers]
      .map(String)
      .filter((id) => !users.has(id) && !inFlightRef.current.has(id));
    if (!unknown.length) return undefined;

    const timer = setTimeout(async () => {
      unknown.forEach((id) => inFlightRef.current.add(id));
      for (let i = 0; i < unknown.length; i += USER_CHUNK) {
        const chunk = unknown.slice(i, i + USER_CHUNK);
        try {
          const res = await listUsersByIds(chunk);
          const list = Array.isArray(res?.data) ? res.data : [];
          const byId = new Map(list.map((u) => [String(u?.id || u?._id), u]));
          if (mountedRef.current) {
            setUsers((prev) => {
              const next = new Map(prev);
              chunk.forEach((id) => next.set(id, byId.get(id) || null));
              return next;
            });
          }
        } catch (error) {
          // Left unresolved rather than cached as nobody, so the next
          // presence change tries these ids again.
          console.error('Online students: could not resolve users', error);
        } finally {
          chunk.forEach((id) => inFlightRef.current.delete(id));
        }
      }
    }, RESOLVE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [scope, ready, failed, onlineUsers, users]);

  const students = useMemo(() => {
    const ids = [...onlineUsers].map(String);
    if (scope === 'trainer') {
      return ids.map((id) => roster.get(id)).filter(Boolean);
    }
    return ids
      .map((id) => users.get(id))
      .filter((u) => isStudentUser(u, studentRole))
      .map((u) => ({
        key: String(u.id || u._id),
        studentId: null,
        name: fullName(u),
        email: u.email || '',
        batchName: '',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [scope, onlineUsers, roster, users, studentRole]);

  useEffect(() => {
    if (!showList) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowList(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showList]);

  const openStudent = async (row) => {
    // A Waypoint opened from inside a popup closes the popup first.
    if (row.studentId) {
      setShowList(false);
      openEntity('student', row.studentId);
      return;
    }
    if (!row.email) {
      setOpening((p) => ({ ...p, [row.key]: 'missing' }));
      return;
    }
    setOpening((p) => ({ ...p, [row.key]: 'busy' }));
    try {
      const resp = await getDashboardTabData({
        tab: 'students',
        page: 1,
        limit: 5,
        search: row.email,
        scope: 'admin',
      });
      const items = resp?.data?.items || [];
      const match = items.find(
        (s) => String(s?.email || '').toLowerCase() === row.email.toLowerCase(),
      );
      if (!mountedRef.current) return;
      if (match?.id) {
        setOpening((p) => {
          const next = { ...p };
          delete next[row.key];
          return next;
        });
        setShowList(false);
        openEntity('student', match.id);
      } else {
        setOpening((p) => ({ ...p, [row.key]: 'missing' }));
      }
    } catch (error) {
      console.error('Online students: could not find the student record', error);
      if (mountedRef.current) setOpening((p) => ({ ...p, [row.key]: 'missing' }));
    }
  };

  const unavailable = ready && failed;
  const count = !ready || unavailable ? '—' : students.length;
  // A zero stays a plain figure rather than opening an empty list.
  const canOpen = ready && !failed && students.length > 0;
  const Tag = canOpen ? 'button' : 'div';
  const tagProps = canOpen
    ? { type: 'button', onClick: () => setShowList(true), title: 'Show who is online' }
    : {};
  const dot = (
    <i className={`onl-dot${connected && !unavailable ? '' : ' onl-dot--off'}`} aria-hidden="true" />
  );
  const status = unavailable ? 'Unavailable' : !connected ? 'Reconnecting…' : 'Live';
  const scopeNote = scope === 'trainer' ? 'in your batches' : 'across the institute';

  const card =
    variant === 'vital' ? (
      <Tag className={`sm-vital sm-vital--teal${canOpen ? ' sm-vital--go' : ''}`} {...tagProps}>
        <div className="sm-vital-head">
          <span className="sm-vital-label">Online Students</span>
          <span className="sm-vital-icon">
            <Radio aria-hidden="true" />
          </span>
        </div>
        <div className="sm-vital-value">{count}</div>
        <div className="sm-vital-meta">
          <span className="onl-live">
            {dot}
            {status}
            <i className="sm-vital-sep" aria-hidden="true" />
            {scopeNote}
          </span>
        </div>
      </Tag>
    ) : (
      <Tag className={`ov-tile${canOpen ? ' ov-tile--open' : ''} onl-tile`} {...tagProps}>
        <span className="ov-tile-label">
          <Radio size={13} aria-hidden="true" />
          Online students
          <span className="onl-tile-status" title={status}>
            {dot}
          </span>
        </span>
        <span className="ov-tile-value">{count}</span>
      </Tag>
    );

  /* Portalled to <body>: a position:fixed veil is measured from the
     nearest transformed ancestor, and the admin overview animates its
     tab body -- same reason ExportDialog portals. */
  const popup =
    showList &&
    createPortal(
      <div className="pg-modal" role="presentation" onClick={() => setShowList(false)}>
        <div
          className="pg-modal-panel pg-modal-panel--wide"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(e) => e.stopPropagation()}
        >
          <header className="pg-modal-head">
            <div className="pg-modal-heading">
              <span className="pg-modal-eyebrow">Students</span>
              <h3 className="pg-modal-title" id={titleId}>
                Online now
              </h3>
              <p className="pg-modal-sub">
                <span className="onl-live">
                  {dot}
                  {students.length} online {scopeNote}
                </span>
              </p>
            </div>
            <button
              type="button"
              className="pg-modal-close"
              aria-label="Close"
              onClick={() => setShowList(false)}
            >
              <X size={16} />
            </button>
          </header>

          <div className="pg-modal-body">
            {students.length === 0 ? (
              <p className="pgm-empty">Nobody is online right now.</p>
            ) : (
              /* The view popup's table (view_popup_rules): the row is the
                 bridge to the student -- click, Enter or Space opens their
                 record through Waypoint, and the popup closes first. It
                 was an lp-dialog-table with a Sr. No column and an "Open"
                 link at the end of each row, an action column a view list
                 does not have. */
              <div className="pgm-tablescroll">
                <table className="pgm-table pgm-table--values">
                  <thead>
                    <tr>
                      <th className="pgm-num">#</th>
                      <th>Student</th>
                      {scope === 'trainer' && <th>Batch</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s, i) => {
                      const state = opening[s.key];
                      const missing = state === 'missing';
                      const busy = state === 'busy';
                      const open = () => {
                        if (!missing && !busy) openStudent(s);
                      };
                      return (
                        <tr
                          key={s.key}
                          {...(missing
                            ? {}
                            : {
                                className: 'pgm-row--open',
                                tabIndex: 0,
                                role: 'button',
                                'aria-label': `Open ${s.name}`,
                                'aria-busy': busy || undefined,
                                onClick: open,
                                onKeyDown: (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    open();
                                  }
                                },
                              })}
                        >
                          <td className="pgm-num">{i + 1}</td>
                          <td>
                            <span className="pgm-person-id">
                              <span className="pgm-person-name">
                                <span className="onl-live">
                                  {dot}
                                  {s.name}
                                </span>
                              </span>
                              <span className="pgm-person-mail">
                                {busy
                                  ? 'Opening…'
                                  : missing
                                    ? 'No student record behind this account'
                                    : s.email || 'No email on record'}
                              </span>
                            </span>
                          </td>
                          {scope === 'trainer' && <td>{s.batchName || '—'}</td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      {card}
      {popup}
    </>
  );
};

export default OnlineStudentsBox;
